import { MIME_TYPES } from '../constants/mimeTypes';

// ============================================================================
// PLATFORM DETECTION
// ============================================================================

/**
 * Detects the platform and browser the app is running on
 * @returns {Object} Platform flags and capabilities
 * 
 * Known issues:
 * - iPad detection relies on maxTouchPoints which can be unreliable
 * - Some Android tablets may be detected as desktop
 */
export const getAudioPlatform = () => {
	const ua = navigator.userAgent || '';
	
	// iOS detection includes iPad (MacIntel + touch)
	const isIOS =
		/iPad|iPhone|iPod/.test(ua) ||
		(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
	
	const isAndroid = /Android/i.test(ua);
	
	// Mobile detection fallback using pointer type
	const isMobile =
		isIOS ||
		isAndroid ||
		(typeof window.matchMedia === 'function' &&
			window.matchMedia('(pointer: coarse)').matches);
	
	// Safari detection (including iOS Safari)
	const isSafari =
		(/^((?!chrome|android).)*safari/i.test(ua) && !/Edg|OPR|Firefox/i.test(ua)) ||
		isIOS;
	
	const isFirefox = /Firefox/i.test(ua);
	const isEdge = /Edg/i.test(ua);

	return {
		isIOS,
		isAndroid,
		isMobile,
		isSafari,
		isFirefox,
		isEdge,
		// iOS and mobile Safari have issues with processed audio
		shouldRecordRawStream: isIOS || (isSafari && isMobile),
		// Mobile devices need lighter processing for performance
		shouldUseLiteProcessing: isIOS || isAndroid,
	};
};

// ============================================================================
// RECORDER CONFIGURATION
// ============================================================================

const RECORDER_CANDIDATES = {
	ios: [
		'audio/mp4',
		'audio/mp4;codecs=mp4a',
		'audio/aac',
		'audio/x-m4a',
		'audio/webm;codecs=opus',
		'audio/webm',
	],
	android: [
		'audio/webm;codecs=opus',
		'audio/webm',
		'audio/ogg;codecs=opus',
		'audio/mp4',
		'audio/mp4;codecs=mp4a',
	],
	desktop: [
		'audio/webm;codecs=opus',
		'audio/webm',
		'audio/ogg;codecs=opus',
		'audio/mp4',
		'audio/mp4;codecs=mp4a',
	],
};

const RECORDER_TIMESLICE = {
	MOBILE: 250,
	DESKTOP: 500,
};

const AUDIO_BITRATE = {
	IOS: 48000,
	MOBILE: 64000,
	DESKTOP: 128000,
};

/**
 * Returns microphone constraints optimized for the platform
 */
export const getMicrophoneConstraints = (platform = getAudioPlatform()) => ({
	audio: {
		echoCancellation: true,
		noiseSuppression: true,
		autoGainControl: true,
		...(platform.isMobile && {
			channelCount: { ideal: 1 },
		}),
	},
});

/**
 * Finds the first supported MIME type for MediaRecorder
 * @returns {string} Supported MIME type or empty string
 */
export const getSupportedRecorderMimeType = (platform = getAudioPlatform()) => {
	if (typeof MediaRecorder === 'undefined') {
		return '';
	}

	const candidates = platform.isIOS
		? [...RECORDER_CANDIDATES.ios, ...MIME_TYPES]
		: platform.isAndroid
			? [...RECORDER_CANDIDATES.android, ...MIME_TYPES]
			: [...RECORDER_CANDIDATES.desktop, ...MIME_TYPES];

	return (
		candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) || ''
	);
};

/**
 * Returns MediaRecorder configuration for the platform
 */
export const getRecorderConfig = (platform = getAudioPlatform()) => {
	const mimeType = getSupportedRecorderMimeType(platform);

	return {
		mimeType,
		timeslice: platform.isMobile
			? RECORDER_TIMESLICE.MOBILE
			: RECORDER_TIMESLICE.DESKTOP,
		audioBitsPerSecond: platform.isIOS
			? AUDIO_BITRATE.IOS
			: platform.isMobile
				? AUDIO_BITRATE.MOBILE
				: AUDIO_BITRATE.DESKTOP,
	};
};

// ============================================================================
// AUDIO CONTEXT MANAGEMENT
// ============================================================================

let sharedAudioContext = null;

/**
 * Gets or creates a shared AudioContext
 * @returns {Promise<AudioContext|null>}
 * 
 * Known issues:
 * - AudioContext may be blocked until user interaction on some browsers
 * - Context can be suspended by browser autoplay policies
 */
export const getSharedAudioContext = async () => {
	const AudioContextClass = window.AudioContext || window.webkitAudioContext;

	if (!AudioContextClass) {
		return null;
	}

	if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
		try {
			sharedAudioContext = new AudioContextClass();
		} catch {
			return null;
		}
	}

	if (sharedAudioContext.state === 'suspended') {
		try {
			await sharedAudioContext.resume();
		} catch {
			return null;
		}
	}

	return sharedAudioContext.state === 'running' ? sharedAudioContext : null;
};

/**
 * Closes the shared AudioContext and cleans up
 */
export const closeSharedAudioContext = async () => {
	if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
		sharedAudioContext = null;
		return;
	}

	try {
		await sharedAudioContext.close();
	} catch {
		// Context may already be closing
	}

	sharedAudioContext = null;
};

// ============================================================================
// STREAM MANAGEMENT
// ============================================================================

/**
 * Stops all tracks in a MediaStream
 */
export const stopMediaStream = (stream) => {
	stream?.getTracks?.().forEach((track) => {
		track.stop();
	});
};

const DEFAULT_MIC_TIMEOUT_MS = 15000;

/**
 * Requests microphone access with timeout
 * @throws {Error} with MICROPHONE_ERROR code
 * 
 * Known issues:
 * - May fail on first call on iOS if no user interaction happened
 * - Timeout may be too short on slow devices or poor network
 */
export const requestMicrophoneStream = async ({
	platform = getAudioPlatform(),
	timeoutMs = DEFAULT_MIC_TIMEOUT_MS,
} = {}) => {
	if (!navigator.mediaDevices?.getUserMedia) {
		throw createMicrophoneError('MICROPHONE_UNSUPPORTED');
	}

	try {
		const getStream = navigator.mediaDevices.getUserMedia(
			getMicrophoneConstraints(platform),
		);
		const timeout = new Promise((_, reject) => {
			window.setTimeout(
				() => reject(createMicrophoneError('MIC_TIMEOUT')),
				timeoutMs,
			);
		});

		return await Promise.race([getStream, timeout]);
	} catch (error) {
		throw createMicrophoneError(resolveMicrophoneErrorCode(error), error);
	}
};

// ============================================================================
// ERROR HANDLING
// ============================================================================

export const MICROPHONE_ERROR = {
	UNSUPPORTED: 'MICROPHONE_UNSUPPORTED',
	DENIED: 'MICROPHONE_DENIED',
	NOT_FOUND: 'MICROPHONE_NOT_FOUND',
	IN_USE: 'MICROPHONE_IN_USE',
	TIMEOUT: 'MIC_TIMEOUT',
	UNKNOWN: 'MICROPHONE_UNKNOWN',
	MIME_TYPE_UNSUPPORTED: 'MIME_TYPE_UNSUPPORTED',
};

const createMicrophoneError = (code, originalError) => {
	const error = new Error(code);
	error.code = code;
	error.originalError = originalError;
	return error;
};

export const resolveMicrophoneErrorCode = (error) => {
	if (!error) {
		return MICROPHONE_ERROR.UNKNOWN;
	}

	if (typeof error.code === 'string' && error.code.startsWith('MICROPHONE_')) {
		return error.code;
	}

	if (error.message === 'MIC_TIMEOUT') {
		return MICROPHONE_ERROR.TIMEOUT;
	}

	if (error.message === 'MICROPHONE_UNSUPPORTED') {
		return MICROPHONE_ERROR.UNSUPPORTED;
	}

	const errorName = error.name || error.originalError?.name;

	if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
		return MICROPHONE_ERROR.DENIED;
	}

	if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
		return MICROPHONE_ERROR.NOT_FOUND;
	}

	if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
		return MICROPHONE_ERROR.IN_USE;
	}

	return MICROPHONE_ERROR.UNKNOWN;
};

// ============================================================================
// AUDIO PROCESSING
// ============================================================================

const AUDIO_PROCESSING = {
	LITE: {
		GAIN: 1.35,
		HIGHPASS_FREQ: 100,
	},
	FULL: {
		GAIN: 1.8,
		HIGHPASS_FREQ: 80,
		LOWPASS_FREQ: 8000,
		COMPRESSOR: {
			THRESHOLD: -30,
			KNEE: 40,
			RATIO: 12,
			ATTACK: 0,
			RELEASE: 0.25,
		},
	},
};

/**
 * Applies lite audio processing (gain + highpass filter)
 * Used on mobile devices for performance
 */
const connectLiteProcessing = (audioContext, source) => {
	const gainNode = audioContext.createGain();
	gainNode.gain.value = AUDIO_PROCESSING.LITE.GAIN;

	const highPassFilter = audioContext.createBiquadFilter();
	highPassFilter.type = 'highpass';
	highPassFilter.frequency.value = AUDIO_PROCESSING.LITE.HIGHPASS_FREQ;

	source.connect(gainNode).connect(highPassFilter);

	const destination = audioContext.createMediaStreamDestination();
	highPassFilter.connect(destination);

	return destination.stream;
};

/**
 * Applies full audio processing (gain + filters + compression)
 * Used on desktop for better audio quality
 */
const connectFullProcessing = (audioContext, source) => {
	const gainNode = audioContext.createGain();
	gainNode.gain.value = AUDIO_PROCESSING.FULL.GAIN;

	const highPassFilter = audioContext.createBiquadFilter();
	highPassFilter.type = 'highpass';
	highPassFilter.frequency.value = AUDIO_PROCESSING.FULL.HIGHPASS_FREQ;

	const lowPassFilter = audioContext.createBiquadFilter();
	lowPassFilter.type = 'lowpass';
	lowPassFilter.frequency.value = AUDIO_PROCESSING.FULL.LOWPASS_FREQ;

	const compressor = audioContext.createDynamicsCompressor();
	compressor.threshold.value = AUDIO_PROCESSING.FULL.COMPRESSOR.THRESHOLD;
	compressor.knee.value = AUDIO_PROCESSING.FULL.COMPRESSOR.KNEE;
	compressor.ratio.value = AUDIO_PROCESSING.FULL.COMPRESSOR.RATIO;
	compressor.attack.value = AUDIO_PROCESSING.FULL.COMPRESSOR.ATTACK;
	compressor.release.value = AUDIO_PROCESSING.FULL.COMPRESSOR.RELEASE;

	source
		.connect(gainNode)
		.connect(highPassFilter)
		.connect(lowPassFilter)
		.connect(compressor);

	const destination = audioContext.createMediaStreamDestination();
	compressor.connect(destination);

	return destination.stream;
};

/**
 * Creates recording pipeline with optional audio processing
 * @returns {Object} { recordStream, monitorStream, audioContext }
 * 
 * Known issues:
 * - Processing may fail on iOS Safari, falls back to raw stream
 * - AudioContext may not be available without user gesture
 */
export const createRecordingPipeline = async (
	rawStream,
	platform = getAudioPlatform(),
) => {
	// iOS and Safari mobile need raw stream
	if (platform.shouldRecordRawStream) {
		return {
			recordStream: rawStream,
			monitorStream: rawStream,
			audioContext: null,
		};
	}

	const audioContext = await getSharedAudioContext();
	if (!audioContext) {
		return {
			recordStream: rawStream,
			monitorStream: rawStream,
			audioContext: null,
		};
	}

	const source = audioContext.createMediaStreamSource(rawStream);
	const processedStream = platform.shouldUseLiteProcessing
		? connectLiteProcessing(audioContext, source)
		: connectFullProcessing(audioContext, source);

	return {
		recordStream: processedStream,
		monitorStream: rawStream,
		audioContext,
	};
};

// ============================================================================
// MEDIA RECORDER
// ============================================================================

/**
 * Creates MediaRecorder with platform-optimized config
 * Falls back to default config if specified options fail
 */
export const createMediaRecorder = (stream, platform = getAudioPlatform()) => {
	const { mimeType, audioBitsPerSecond } = getRecorderConfig(platform);
	const options = {};

	if (mimeType) {
		options.mimeType = mimeType;
	}

	if (audioBitsPerSecond) {
		options.audioBitsPerSecond = audioBitsPerSecond;
	}

	try {
		return options.mimeType
			? new MediaRecorder(stream, options)
			: new MediaRecorder(stream);
	} catch {
		return new MediaRecorder(stream);
	}
};

/**
 * Builds recording blob from chunks
 */
export const buildRecordingBlob = (chunks, mimeType = '') => {
	const validChunks = chunks.filter((chunk) => chunk?.size > 0);

	if (validChunks.length === 0) {
		return null;
	}

	const resolvedType =
		mimeType ||
		validChunks.find((chunk) => chunk.type)?.type ||
		getSupportedRecorderMimeType() ||
		'audio/webm';

	return new Blob(validChunks, { type: resolvedType });
};

const RECORDER_STOP_DELAY = {
	IOS_SAFARI: 120,
	DEFAULT: 60,
};

const platformStopDelay = () => {
	const { isIOS, isSafari } = getAudioPlatform();
	return isIOS || isSafari
		? RECORDER_STOP_DELAY.IOS_SAFARI
		: RECORDER_STOP_DELAY.DEFAULT;
};

/**
 * Stops MediaRecorder safely and returns final blob
 * Adds platform-specific delays to ensure all data is captured
 */
export const stopMediaRecorderSafely = (recorder, chunksRef) =>
	new Promise((resolve) => {
		if (!recorder || recorder.state === 'inactive') {
			resolve(buildRecordingBlob(chunksRef.current, recorder?.mimeType));
			return;
		}

		const finalize = () => {
			window.setTimeout(() => {
				resolve(buildRecordingBlob(chunksRef.current, recorder.mimeType));
			}, platformStopDelay());
		};

		recorder.onstop = finalize;

		try {
			if (typeof recorder.requestData === 'function') {
				recorder.requestData();
			}
			recorder.stop();
		} catch {
			finalize();
		}
	});

// ============================================================================
// SPEECH DETECTION
// ============================================================================

/**
 * Returns speech detection parameters tuned for each platform
 * These values are empirically tuned for best results
 */
export const getSpeechDetectionOptions = (platform = getAudioPlatform()) => {
	if (platform.isIOS) {
		return {
			calibrationMs: 480,
			minSpeechMs: 320,
			silenceMs: 1000,
			activityHoldMs: 280,
			minActiveMs: 180,
			maxListenMs: 30000,
			pollMs: 48,
			speechStartMultiplier: 3.8,
			speechContinueMultiplier: 1.22,
			earlySpeechMultiplier: 2.4,
			minThreshold: 0.0055,
		};
	}

	if (platform.isAndroid) {
		return {
			calibrationMs: 420,
			minSpeechMs: 340,
			silenceMs: 1100,
			activityHoldMs: 300,
			minActiveMs: 170,
			maxListenMs: 30000,
			pollMs: 45,
			speechStartMultiplier: 4.1,
			speechContinueMultiplier: 1.28,
			earlySpeechMultiplier: 2.5,
			minThreshold: 0.0065,
		};
	}

	if (platform.isMobile) {
		return {
			calibrationMs: 400,
			minSpeechMs: 360,
			silenceMs: 1150,
			activityHoldMs: 300,
			minActiveMs: 170,
			maxListenMs: 30000,
			pollMs: 45,
			speechStartMultiplier: 4.2,
			speechContinueMultiplier: 1.32,
			earlySpeechMultiplier: 2.5,
			minThreshold: 0.007,
		};
	}

	return {
		calibrationMs: 380,
		minSpeechMs: 380,
		silenceMs: 1250,
		activityHoldMs: 320,
		minActiveMs: 160,
		maxListenMs: 30000,
		pollMs: 45,
		speechStartMultiplier: 4.4,
		speechContinueMultiplier: 1.36,
		earlySpeechMultiplier: 2.6,
		minThreshold: 0.009,
	};
};

// ============================================================================
// AUDIO PLAYBACK
// ============================================================================

/**
 * Decodes base64 string to Blob
 */
export const decodeBase64ToBlob = (
	base64,
	mimeType = 'application/octet-stream',
) => {
	const binary = atob(base64);
	const bytes = new Uint8Array(binary.length);

	for (let index = 0; index < binary.length; index += 1) {
		bytes[index] = binary.charCodeAt(index);
	}

	return new Blob([bytes], { type: mimeType });
};

/**
 * Plays audio blob and waits for completion
 * @returns {Promise<boolean>} true if played successfully
 * 
 * Known issues:
 * - iOS requires explicit load() call
 * - Playback may fail due to autoplay policies
 * - URL.createObjectURL must be revoked to prevent memory leaks
 */
export const playAudioBlob = async (blob, platform = getAudioPlatform()) => {
	if (!blob) {
		return false;
	}

	const objectUrl = URL.createObjectURL(blob);
	const audio = new Audio();
	audio.src = objectUrl;
	audio.preload = 'auto';
	audio.playsInline = true;
	audio.setAttribute('playsinline', '');
	audio.setAttribute('webkit-playsinline', '');

	// iOS requires explicit load
	if (platform.isIOS) {
		audio.load();
	}

	try {
		await audio.play();
	} catch {
		URL.revokeObjectURL(objectUrl);
		return false;
	}

	await new Promise((resolve) => {
		const finish = () => {
			URL.revokeObjectURL(objectUrl);
			resolve();
		};

		audio.onended = finish;
		audio.onerror = finish;
	});

	return true;
};
